import React from 'react';
import { Users, Luggage } from 'lucide-react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import useIsMobile from '@/hooks/useIsMobile';

const IMAGES = [
  'https://static.prod-images.emergentagent.com/jobs/79680a12-b72c-4e4d-9800-308b06c8b20c/images/4040d453e14542f95833baaf6cb2e652d496760c74c2b8c61de76d9ff33f63a6.png',
  'https://static.prod-images.emergentagent.com/jobs/79680a12-b72c-4e4d-9800-308b06c8b20c/images/2045a73cc6caaf8a20186b90154de67930ea720eb186e1c44e155ecae7196d5b.png',
];

const Fleet = () => {
  const isMobile = useIsMobile();
  const { t } = useTranslation();
  const vehicles = t('fleet_vehicles', { returnObjects: true });

  return (
    <section id="flotte" className="bg-[#0F0F0F] py-20 px-4">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <p className="text-xs uppercase tracking-[0.2em] text-[#EAB308] font-semibold mb-3">{t('fleet.label')}</p>
          <h2 className="text-3xl sm:text-4xl font-light text-white tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {t('fleet.title')}
          </h2>
          <p className="text-[#A3A3A3] mt-3 text-base max-w-xl mx-auto">{t('fleet.subtitle')}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {vehicles.map((v, i) => (
            <motion.div
              key={v.name}
              {...(!isMobile && {
                initial: { opacity: 0, x: i === 0 ? -20 : 20 },
                whileInView: { opacity: 1, x: 0 },
                viewport: { once: true },
                transition: { duration: 0.6, delay: i * 0.1 },
              })}
              data-testid={`fleet-vehicle-${i + 1}`}
              className="bg-[#1A1A1A] border border-white/5 rounded-2xl overflow-hidden group hover:border-white/15 transition-all duration-300"
            >
              <div className="relative h-56 sm:h-64 overflow-hidden">
                <img src={IMAGES[i]} alt={`${v.name} - Taxi Yellow Cab Corsica`} loading="lazy"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#1A1A1A] via-transparent to-transparent" />
                <div className="absolute top-4 left-4">
                  <span className="bg-[#EAB308] text-black text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wide">{v.category}</span>
                </div>
              </div>

              <div className="p-6">
                <h3 className="text-xl font-semibold text-white mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>{v.name}</h3>
                <p className="text-[#A3A3A3] text-sm mb-4">{v.desc}</p>
                <div className="flex gap-4 mb-5">
                  <div className="flex items-center gap-2 text-sm">
                    <Users size={16} className="text-[#EAB308]" />
                    <span className="text-white font-medium">{v.passengers} {t('fleet.passengers')}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Luggage size={16} className="text-[#EAB308]" />
                    <span className="text-white font-medium">{v.luggage} {t('fleet.luggage')}</span>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  {v.features.map((f) => (
                    <span key={f} className="text-xs bg-white/5 border border-white/8 text-[#A3A3A3] px-3 py-1 rounded-full">{f}</span>
                  ))}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Fleet;
